/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

/* PACKAGES */
import { Helmet } from "react-helmet";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";

/* COMPONENTS */
import Input from "components/input";
import { Button, Dialog, DialogBody } from "@material-tailwind/react";

/* API */
import {
  deleteStartupCellContactUs,
  getStartupCellContactUs,
} from "service/api";

/* ICONS */
import { FaTrash } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";

/* LOADING SCREEN FOR DATATABLE */
const LoadingScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-semibold text-black text-center">
        Please wait!
      </p>
      <p className="text-sm font-ibm-semibold text-black text-center">
        We are fetching data from the database...
      </p>
    </div>
  );
};

/* NO DATA SCREEN */
const NoDataScreen = () => {
  return (
    <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
      <p className="text-sm font-ibm-semibold text-black text-center">
        There are no queries to display.
      </p>
    </div>
  );
};

const StartupContactUs = () => {
  /* LOADING STATE */
  const [loading, setLoading] = useState(false);

  /* PAGINATION */
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);

  /* TOTAL DOCUMENTS */
  const [totalDocs, setTotalDocs] = useState(0);

  /* QUERIES */
  const [contactQueries, setContactQueries] = useState([]);

  /* SEARCHBAR */
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  /* DELETE MODAL */
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  /* HANDLE PAGE CHANGE */
  const handlePageChange = async (e) => {
    setPage(e);
  };

  /* HANDLE PER PAGE CHANGE */
  const handlePerPageChange = async (e) => {
    setSizePerPage(e);
  };

  /* GET DATA FUNCTION */
  const getDetailsFn = async (search) => {
    setLoading(true);

    const payload = {
      page: page,
      sizePerPage: sizePerPage,
      pagination: true,
    };

    if (search) {
      payload.search = search;
    }

    try {
      const response = await getStartupCellContactUs(payload);
      setContactQueries(response?.data?.docs);
      setTotalDocs(response?.data?.totalDocs);
    } catch (e) {
      console.log("Error: ", e);
    } finally {
      setLoading(false);
    }
  };

  /* HANDLE DELETE FN */
  const handleDelete = () => {
    setDeleteLoading(true);

    deleteStartupCellContactUs(deleteId)
      .then(() => {
        toast.success("The query has been deleted successfully.");
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        getDetailsFn();
      });
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Sr. No",
      grow: 0.5,
      selector: (row, index) => {
        return (
          <p className="text-sm font-source-regular text-greyText">
            {index + 1}
          </p>
        );
      },
    },
    {
      name: "Name",
      selector: (row) => {
        return (
          <p className="text-sm font-source-semibold text-black">
            {row?.firstName} {row?.lastName}
          </p>
        );
      },
    },
    {
      name: "Email address",
      selector: (row) => {
        return (
          <>
            <a
              href={`mailto:${row?.email}`}
              className="block text-sm font-source-regular text-blue-500"
            >
              {row?.email}
            </a>
            <a
              href={`mailto:${row?.phoneNumber}`}
              className="block mt-1 text-sm font-source-regular text-blue-500"
            >
              {row?.phoneNumber}
            </a>
          </>
        );
      },
    },
    {
      name: "Message",
      grow: 1.5,
      selector: (row) => {
        return (
          <p
            style={{ overflowWrap: "anywhere" }}
            className="text-sm font-source-regular text-greyText pr-5"
          >
            {row?.message}
          </p>
        );
      },
    },
    {
      name: "Actions",

      selector: (row) => {
        return (
          <button
            onClick={() => {
              setDeleteModal(!deleteModal);
              setDeleteId(row?._id);
            }}
            className="p-2.5 bg-white rounded-full hover:bg-red-500/10 transition-all duration-300 ease-in-out"
          >
            <FaTrash className="w-4 h-4 text-greyText" />
          </button>
        );
      },
    },
  ];

  /* USE EFFECT */
  useEffect(() => {
    getDetailsFn(debouncedSearch);
  }, [debouncedSearch]);

  /* USE EFFECT */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <section className="p-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Contact Us Queries | Lohana Startup Cell</title>
      </Helmet>

      <div className="container-full-width">
        {/* HEADING */}
        <div className="block xl:flex lg:flex items-center justify-between">
          <div className="p-0">
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Contact Us
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage the queries by your visitors from your startup cell
              website.
            </h2>
          </div>

          {/* SEARCHBAR */}
          <div className="w-full xl:w-[30vh] lg:w-[30vh] mt-2 xl:mt-0 lg:mt-0">
            <Input
              type="text"
              placeholder="Search by name..."
              name="search"
              id="search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            columns={columns}
            data={contactQueries}
            progressPending={loading}
            progressComponent={<LoadingScreen />}
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            onChangeRowsPerPage={handlePerPageChange}
            onChangePage={handlePageChange}
            noDataComponent={<NoDataScreen />}
          />
        </div>
      </div>

      {/* DELETE DIALOG */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Delete information
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setDeleteModal(!deleteModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <div className="p-5 border-b border-authBorder">
            <p className="text-base font-ibm-regular text-black">
              Are you sure you want to delete this contact query from the
              website?
            </p>
          </div>
          <div className="flex items-center justify-end px-5 py-3">
            <Button
              onClick={handleDelete}
              className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
            >
              {deleteLoading ? "Please wait.." : "Delete"}
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default StartupContactUs;
